import {
  Component,
  computed,
  Inject,
  inject,
  OnInit,
  Optional,
  Type,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@softline/core';

import { switchMap } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { UserInfoService } from '@softline/auth';
import {
  IconComponent,
  LegacyModalOption,
} from '@softline/ui-core';
import { RefreshService } from '@softline/application';
import {
  AccountContextConfiguration,
  SOFTLINE_CONFIG_CONTEXT_ACCOUNT,
  SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT,
  SOFTLINE_FEATURE_UTILITIES_ACCOUNT,
  UtilitiesAccountStore,
} from '../../../../utilities.shared';
import { Account } from '../../data/account';
import { PatchContextService } from '../../../services/patch-context.service';
import { SoftAppsUserInfo } from '@softapps/core';

@Component({
  selector: 'soft-account-context-widget',
  imports: [CommonModule, IconComponent],
  templateUrl: './account-context-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./account-context-widget.component.css'],
})
export class AccountContextWidgetComponent implements OnInit {
  private readonly userInfo = inject<UserInfoService<SoftAppsUserInfo>>(UserInfoService);
  private readonly store = inject(Store);

  userAccounts$ = this.store.observe(
    SOFTLINE_FEATURE_UTILITIES_ACCOUNT,
    UtilitiesAccountStore.getters.all
  );

  private readonly context$ = toObservable(computed(() => this.userInfo.value()));

  activeUserAccount$ = this.context$.pipe(
    switchMap((context) =>
      this.store.observe(
        SOFTLINE_FEATURE_UTILITIES_ACCOUNT,
        UtilitiesAccountStore.getters.entity,
        context?.accountid
      )
    )
  );

  constructor(
    private refreshService: RefreshService,
    private patchContextService: PatchContextService,
    @Inject(SOFTLINE_CONFIG_CONTEXT_ACCOUNT)
    readonly accountContextConfig: AccountContextConfiguration,
    @Optional()
    @Inject(SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT)
    public components?: Type<Component>[]
  ) {}

  async ngOnInit(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_UTILITIES_ACCOUNT,
      UtilitiesAccountStore.actions.loadMany,
      { clear: true }
    );
  }

  async onChangeAccount(accounts: Account[]): Promise<void> {
    const options = accounts.map<LegacyModalOption<string>>((o) => ({
      value: o?.id + '',
      label: o?.number ? `${o?.number} - ${o?.name}` : `${o?.name ?? ''}`,
    }));

    if (this.components && this.components.length > 1) {
      options.push({
        value: 'FIRMENWECHSEL_OEFFNEN',
        label: '#UTILITIES.CONTEXT.DETAIL_CONTEXT_SETTINGS',
        icon: 'fa-regular fa-user-gear',
      });
    }

    await this.patchContextService.patchContextOptionsDialog<string>({
      title: 'Konto auswählen',
      options,
      editContextDialogOptionValue: 'FIRMENWECHSEL_OEFFNEN',
      setOnSelect: (contextResult) => {
        const context = this.userInfo.value();

        if (context?.accountids) {
          return {
            accountid: +contextResult,
            accountids: [+contextResult],
          };
        }

        return {
          accountid: +contextResult,
        };
      },
    });
  }
}
